import store from '@/store';
import axios from "@/libs/axios";
import { computed,ref, watch } from '@vue/composition-api';
import useFilterCounter from '@/components/Filters/useFiltersCount';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import moment from 'moment';
import {
  HANWHA_EVENT_TYPES_BY_LOG,
  HANWHA_LOG_TYPE_OPTIONS,
} from './catalogs';

export default function useList() {
  //STORE
  // The focused-unit slot must match against every DVR, not only the API's
  // default page.
  store.dispatch('hanwha_dvr/getAll', { max: 1000 });

  //COMPOASBLES
  const toast = useToast();

  //DATA
  const currentPage = ref(1);

  const dvrFilter = ref(null);
  const typeLogFilter = ref([]);
  const typeFilter = ref([]);
  const cameraFilter = ref([]);
  const startDatetimeFilter = ref(moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'));
  const endDatetimeFilter = ref(moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'));

  const filters = ref({
    start_date: startDatetimeFilter.value,
    end_date: endDatetimeFilter.value,
  });

  const filtersCount = useFilterCounter(filters);

  const isSortDirDesc = ref(true);

  const perPage = ref(10);

  const refListTable = ref(null)

  const tableColumns = [
    { label: 'Dvr', key: 'dvr.name' },
    { label: 'Tipo de log', key: 'type_log' },
    { label: 'Tipo', key: 'type' },
    { label: 'Descripción', key: 'description'},
    { label: 'Fecha', key: 'date' }
  ];

  const totalList = ref(0);

  const perPageOptions = [10, 25, 50, 100];

  const searchQuery = ref('');

  const sortBy = ref('date');

  const exporting = ref(false);

  //COMPUTED
  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value?.$refs?.refTable?.localItems?.length || 0;

    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  });

  const dvrOptions = computed(() => store.getters['hanwha_dvr/getSelectedItems'])
  const cameraOptions = ref([]);
  const typeLogOptions = HANWHA_LOG_TYPE_OPTIONS;
  const typesByLog = HANWHA_EVENT_TYPES_BY_LOG;
  const typeOptions = computed(() => {
    const selectedLogTypes = typeLogFilter.value || [];
    const options = selectedLogTypes
      .flatMap(typeLog => typesByLog[typeLog] || [])
      .map(type => ({ label: type, value: type }));

    return options.filter((option, index, all) =>
      all.findIndex(item => item.value === option.value) === index
    );
  });

  //METHODS
  const buildFilters = () => {
    filters.value = {
      dvr: dvrFilter.value,
      type_log: typeLogFilter.value,
      type: typeFilter.value,
      camera: cameraFilter.value,
      start_date: startDatetimeFilter.value,
      end_date: endDatetimeFilter.value,
    };

    refetchData();
  }

  const clearSearchFilters = () => {
    dvrFilter.value = null;
    typeLogFilter.value = [];
    typeFilter.value = [];
    cameraFilter.value = [];
    startDatetimeFilter.value = null;
    endDatetimeFilter.value = null;

    buildFilters();
  }

  const toQueryValue = value => {
    if (!Array.isArray(value)) return value;
    if (value.length === 0) return undefined;
    return value.length === 1 ? value[0] : value;
  }

  const getRequestParams = () => ({
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      dvr: dvrFilter.value,
      type_log: toQueryValue(typeLogFilter.value),
      type: toQueryValue(typeFilter.value),
      camera: toQueryValue(cameraFilter.value),
      start_date: startDatetimeFilter.value,
      end_date: endDatetimeFilter.value,
  });

  const fetchList = (ctx, callback) => {
    axios.get('v1/hanwha/logs', { params: getRequestParams() })
    .then(response => {
      callback(response.data.data);
      totalList.value = response.data.meta.total
    })
    .catch((error) => {
      console.log('error', error);
    })
  }

  const getExportFilename = contentDisposition => {
    const encodedFilename = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    if (encodedFilename) return decodeURIComponent(encodedFilename);

    return contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1]
      || 'hanwha-logs.xlsx';
  }

  const exportList = async () => {
    exporting.value = true;

    try {
      const response = await axios.get('v1/hanwha/logs', {
        params: {
          ...getRequestParams(),
          action: 'export',
        },
        responseType: 'blob',
      });
      const filename = getExportFilename(response.headers['content-disposition']);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Error al exportar los logs de Hanwha',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      });
      console.error('Error exporting Hanwha logs:', error);
    } finally {
      exporting.value = false;
    }
  }

  const refetchData = () => {
    refListTable.value?.$refs?.refTable?.refresh()
  }

  //WATCHERS
  watch([currentPage, perPage], () => {
    refetchData()
  });

  let cameraRequestId = 0;
  watch(dvrFilter, async selectedDvrs => {
    const requestId = ++cameraRequestId;
    cameraFilter.value = [];

    if (!selectedDvrs || selectedDvrs.length === 0) {
      cameraOptions.value = [];
      return;
    }

    try {
      const response = await store.dispatch('hanwha_camera/getAll', {
        dvr: selectedDvrs,
        max: 1000,
      });

      if (requestId !== cameraRequestId) return;

      cameraOptions.value = (response?.data || [])
        .filter(camera => camera.device?.id)
        .map(camera => ({
          value: camera.device.id,
          label: camera.name,
        }));
    } catch (error) {
      if (requestId === cameraRequestId) cameraOptions.value = [];
      console.log('error', error);
    }
  });

  watch(typeLogFilter, () => {
    const validTypes = new Set(typeOptions.value.map(option => option.value));
    typeFilter.value = typeFilter.value.filter(type => validTypes.has(type));
  });

  return {
    currentPage,
    dvrFilter,
    typeLogFilter,
    typeFilter,
    cameraFilter,
    startDatetimeFilter,
    endDatetimeFilter,
    filters,
    filtersCount,
    isSortDirDesc,
    perPage,
    refListTable,
    tableColumns,
    totalList,
    perPageOptions,
    searchQuery,
    sortBy,

    dataMeta,
    dvrOptions,
    typeLogOptions,
    typeOptions,
    cameraOptions,

    buildFilters,
    clearSearchFilters,
    fetchList,
    exportList,
    exporting,
    refetchData
  }
}
